"use client";

import { useCallback } from "react";

/** Wohin eine App nach dem Abmelden geht, wenn sie nichts anderes sagt. */
export const ABMELDE_ZIEL_STANDARD = "/auth/login";

/**
 * Der Abmelde-Vorgang der Suite — EINE Fassung fuer alle Apps.
 *
 * WARUM ZENTRAL: `AppLayout.onLogout` war optional, und `AccountMenuItems`
 * rendert den Eintrag unter einem harten `&&`. Fehlte die Prop, verschwand das
 * Abmelden aus TopNav-Popup, Sidebar-Popup und MobileNav-Fuss **gleichzeitig** —
 * ohne Fehler, ohne Warnung, ohne Test. Genau so war `janeway-brain`: kein
 * einziger Treffer fuer `signOut`/`Abmelden`/`logout` in `app/`, `components/`,
 * `lib/`. Wer dort arbeitete, kam nur ueber eine Nachbar-App oder die
 * Keksverwaltung wieder heraus (GUI-A-08, GUI-B-06).
 *
 * WARUM EIN HARTER SEITENWECHSEL statt `router.push`: Nach `signOut` ist die
 * Sitzung weg, der React-Baum aber nicht — Server-Komponenten-Caches, Provider-
 * Zustand und die geladene Arbeitsbereichsliste des abgemeldeten Nutzers leben
 * bei einer Client-Navigation weiter. `location.assign` verwirft alles und
 * laesst die Middleware neu entscheiden. Die 16 Apps, die den Vorgang bisher von
 * Hand schrieben, nehmen `router.push` — das ist der einzige Punkt, in dem diese
 * Fassung von ihnen abweicht, und der Grund steht hier.
 *
 * WARUM NICHT `useUser().logout`: `UserContext` hat einen Vorgabewert, in dem
 * `logout` ein `noop` ist. Eine App ohne `UserProvider` bekaeme damit einen
 * Knopf, der nichts tut — schlechter als kein Knopf, weil niemand es merkt.
 * Diese Fassung haengt an keinem Provider.
 */
export function useAbmelden(ziel: string = ABMELDE_ZIEL_STANDARD): () => Promise<void> {
  return useCallback(async () => {
    // Dynamisch, damit der Supabase-Browser-Client nicht in jedem Bundle liegt,
    // das `AppLayout` importiert.
    const { createSupabaseBrowser } = await import("@janeway/app-sdk/supabase");
    try {
      await createSupabaseBrowser().auth.signOut();
    } catch (fehler) {
      // Ein fehlgeschlagener Netzaufruf darf den Nutzer nicht auf der Seite
      // festhalten — das lokale Sitzungskeks ist danach ohnehin verworfen, und
      // die Anmeldeseite ist der sichere Zustand.
      console.warn("[abmelden] signOut fehlgeschlagen, gehe trotzdem zum Ziel", fehler);
    }
    window.location.assign(ziel);
  }, [ziel]);
}
